import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="max-w-md mx-auto px-6 py-14">
      <div className="bg-white dark:bg-neutral-900 border border-[#D6D2C9] dark:border-neutral-700 p-8" style={{ borderRadius: 8 }}>
        <h1 className="font-display text-3xl">Forgot password</h1>
        <p className="mt-2 text-sm text-[color:var(--umg-text-2)]">Enter your email and we'll send a reset link.</p>
        {sent ? (
          <div data-testid="forgot-sent" className="mt-6 p-4 bg-[#EAE6DF] dark:bg-neutral-800 text-sm" style={{ borderRadius: 6 }}>
            If an account exists for <b>{email}</b>, a reset link has been sent. Check inbox and spam.
            <div className="mt-3"><Link to="/auth" className="text-[#C85A53] underline">Back to sign in</Link></div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label>Email</Label>
              <Input data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2" />
            </div>
            <Button data-testid="forgot-submit" type="submit" disabled={busy} className="w-full btn-pill bg-[#C85A53] hover:bg-[#A94842] text-white h-11">
              {busy ? "…" : "Send reset link"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}

export function ResetPassword() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      toast.success("Password reset. Please sign in.");
      nav("/auth");
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="max-w-md mx-auto px-6 py-14">
      <div className="bg-white dark:bg-neutral-900 border border-[#D6D2C9] dark:border-neutral-700 p-8" style={{ borderRadius: 8 }}>
        <h1 className="font-display text-3xl">Set new password</h1>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label>New password</Label>
            <Input data-testid="reset-pw" type="password" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} className="mt-2" />
          </div>
          <div>
            <Label>Confirm password</Label>
            <Input data-testid="reset-pw2" type="password" required minLength={6} value={pw2} onChange={(e) => setPw2(e.target.value)} className="mt-2" />
          </div>
          <Button data-testid="reset-submit" type="submit" disabled={busy || !token} className="w-full btn-pill bg-[#C85A53] hover:bg-[#A94842] text-white h-11">
            {busy ? "…" : "Save password"}
          </Button>
        </form>
      </div>
    </div>
  );
}

export function VerifyEmail() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const [status, setStatus] = useState("verifying");
  React.useEffect(() => {
    if (!token) { setStatus("error"); return; }
    api.post("/auth/verify-email", { token })
      .then(() => setStatus("ok"))
      .catch(() => setStatus("error"));
  }, [token]);
  return (
    <div className="max-w-md mx-auto px-6 py-14 text-center">
      <div className="bg-white dark:bg-neutral-900 border border-[#D6D2C9] dark:border-neutral-700 p-10" style={{ borderRadius: 8 }}>
        {status === "verifying" && <p>Verifying…</p>}
        {status === "ok" && (
          <div data-testid="verify-ok">
            <h2 className="font-display text-3xl">Email verified ✓</h2>
            <p className="mt-3 text-sm text-[color:var(--umg-text-2)]">You're all set.</p>
            <Link to="/home" className="mt-6 inline-block text-[#C85A53] underline">Go to Umaang</Link>
          </div>
        )}
        {status === "error" && (
          <div data-testid="verify-fail">
            <h2 className="font-display text-3xl">Link invalid or expired</h2>
            <Link to="/auth" className="mt-6 inline-block text-[#C85A53] underline">Sign in</Link>
          </div>
        )}
      </div>
    </div>
  );
}
